import { api } from '../lib/api'
import type { ClientInsight, InsightWeight } from '../lib/types'

export interface InsightInput {
  content: string
  category?: string
  weight?: InsightWeight
  validUntil?: string | null // AAAA-MM-DD — fato temporário
}

export const insightsApi = {
  list: (clientId: string) =>
    api.get<{ insights: ClientInsight[] }>(`/clients/${clientId}/insights`),
  create: (clientId: string, data: InsightInput) =>
    api.post<{ insight: ClientInsight }>(`/clients/${clientId}/insights`, data),
  update: (
    clientId: string,
    insightId: string,
    data: Partial<InsightInput> & { active?: boolean }
  ) => api.put<{ insight: ClientInsight }>(`/clients/${clientId}/insights/${insightId}`, data),
  remove: (clientId: string, insightId: string) =>
    api.del<{ ok: true }>(`/clients/${clientId}/insights/${insightId}`),
  interviews: (clientId: string) =>
    api.get<{ interviews: ClientInterview[] }>(`/clients/${clientId}/interviews`),
}

/** Entrevista (ou conversa que gravou fato) do cliente, com os fatos que a IA salvou nela. */
export interface ClientInterview {
  id: string
  mode: 'chat' | 'interview'
  reportId: string | null
  userId: string
  createdAt: string
  updatedAt: string
  user: { name: string }
  answers: number // respostas do gerente (sem a abertura automática da entrevista)
  facts: Array<
    Pick<ClientInsight, 'id' | 'content' | 'category' | 'weight' | 'active' | 'status' | 'validUntil' | 'createdAt'>
  >
}

// Seções do dossiê — espelham INSIGHT_SECTIONS em src/services/intelligence.ts.
// `core` = as 7 seções que medem o nível de conhecimento da IA sobre o cliente.
export const INSIGHT_CATEGORIES: { value: string; label: string; hint: string; core?: boolean }[] = [
  { value: 'valor_ltv', label: 'Economia do cliente', hint: 'ticket médio, margem, valor de um lead ou venda, CPA/ROAS de equilíbrio, LTV', core: true },
  { value: 'restricao', label: 'Restrições e preferências', hint: 'o que o cliente não quer ou não pode (preço, marca, verba, aprovação)', core: true },
  { value: 'funciona', label: 'O que funciona', hint: 'campanhas, públicos, termos e ofertas com resultado comprovado', core: true },
  { value: 'nao_funciona', label: 'O que não funciona / já testado', hint: 'o que já foi tentado e não deu certo', core: true },
  { value: 'palavra_chave', label: 'Termos e palavras-chave', hint: 'termos protegidos (core) e negativas já feitas', core: true },
  { value: 'estrutura', label: 'Estrutura da conta', hint: 'papel de cada campanha, testes, pausas de propósito', core: true },
  { value: 'sazonalidade', label: 'Sazonalidade e calendário', hint: 'picos, quedas, datas comerciais', core: true },
  { value: 'publico', label: 'Público', hint: 'quem compra, regiões, perfis que convertem' },
  { value: 'criativo', label: 'Criativo e oferta', hint: 'mensagens, formatos e ofertas' },
  { value: 'orcamento', label: 'Orçamento', hint: 'limites de verba e como o cliente decide' },
  { value: 'hipotese', label: 'Hipóteses em aberto', hint: 'suspeitas ainda não confirmadas' },
  { value: 'geral', label: 'Geral', hint: '' },
]

export const INSIGHT_WEIGHTS: { value: InsightWeight; label: string }[] = [
  { value: 'context', label: 'Contexto' },
  { value: 'absolute', label: 'Verdade absoluta' },
]

export function insightCategoryLabel(value: string): string {
  return INSIGHT_CATEGORIES.find((c) => c.value === value)?.label ?? value
}

export const INSIGHT_SOURCE_LABEL: Record<string, string> = {
  manual: 'escrito pelo gerente',
  curator: 'aprendido pela IA',
  interview: 'entrevista',
  chat: 'conversa',
  question: 'resposta a pergunta',
  site: 'lido no site',
}
